import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import type { ContentIntakeMode, ContentIntakeSourceResult, PendingPackagePublicationStatus } from '@domain/content-intake';
import { Modal } from '@/app/settings/integrations/components/Modal';
import { INTAKE_MODE_TABS } from './types';
import { formatIntakeError } from './formatError';

interface Props {
  publicationId: string;
  initialMode: ContentIntakeMode;
  onClose: () => void;
  /** Called once a Source is created/found so the caller can refresh lists etc. */
  onCreated?: (result: ContentIntakeSourceResult) => void;
}

const URL_STEPS = ['Fetching article', 'Extracting content', 'Reading metadata', 'Saving Source'];
const PASTE_STEPS = ['Saving Source'];
const SEO_STEPS = ['Parsing package', 'Saving package'];

/** Cycles through step labels for the duration of an in-flight request — never fake percentages. */
function useProgressLabel(active: boolean, steps: string[]): string | null {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setIndex((i) => Math.min(i + 1, steps.length - 1));
    }, 900);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return active ? (steps[index] ?? steps[steps.length - 1] ?? null) : null;
}

export function AddContentModal({ publicationId, initialMode, onClose, onCreated }: Props) {
  const [mode, setMode] = useState<ContentIntakeMode>(initialMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ContentIntakeSourceResult | null>(null);

  // Add URL
  const [url, setUrl] = useState('');

  // Paste Content
  const [content, setContent] = useState('');
  const [pasteSourceUrl, setPasteSourceUrl] = useState('');

  // SEO Package
  const [rawPackage, setRawPackage] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [publicationStatus, setPublicationStatus] = useState<PendingPackagePublicationStatus>('not_published');
  const [packageSaved, setPackageSaved] = useState(false);

  const urlProgress = useProgressLabel(busy && mode === 'url', URL_STEPS);
  const pasteProgress = useProgressLabel(busy && mode === 'paste', PASTE_STEPS);
  const seoProgress = useProgressLabel(busy && mode === 'seo_package', SEO_STEPS);

  function switchMode(next: ContentIntakeMode) {
    setMode(next);
    setError(null);
    setResult(null);
    setPackageSaved(false);
  }

  async function onImportUrl() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await api.importContentFromUrl(publicationId, url.trim());
      setResult(res.item);
      onCreated?.(res.item);
    } catch (err) {
      setError(formatIntakeError(err, "We couldn't retrieve this article."));
    } finally {
      setBusy(false);
    }
  }

  async function onPasteContent() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await api.pasteContent(publicationId, content, pasteSourceUrl.trim() || null);
      setResult(res.item);
      onCreated?.(res.item);
    } catch (err) {
      setError(formatIntakeError(err, 'Spox could not save this content. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function onAddSeoPackage() {
    setError(null);
    setBusy(true);
    try {
      await api.addSeoPackage({
        publicationId,
        rawPackage,
        articleUrl: articleUrl.trim() || null,
        publicationStatus,
      });
      setPackageSaved(true);
    } catch (err) {
      setError(formatIntakeError(err, 'Spox could not save this package. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  const canImportUrl = url.trim().length > 0 && !busy;
  const canPaste = content.trim().length > 0 && !busy;
  const canAddPackage = rawPackage.trim().length > 0 && !busy;

  return (
    <Modal title="Add Content" onClose={onClose} testId="ci-add-content-modal" wide>
      <div className="ig-provider-tabs" role="tablist" aria-label="Add Content mode">
        {INTAKE_MODE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            className={`ig-provider-tab${mode === tab.id ? ' is-active' : ''}`}
            data-testid={`ci-mode-${tab.id}`}
            onClick={() => switchMode(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {mode === 'url' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (canImportUrl) void onImportUrl();
          }}
        >
          <p className="muted">Import a published article from any website.</p>
          <div className="field">
            <label htmlFor="ci-url">Article URL</label>
            <input
              id="ci-url"
              type="url"
              placeholder="https://example.com/article"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setResult(null);
                setError(null);
              }}
              required
            />
          </div>

          {error ? <p className="error">{error}</p> : null}
          {urlProgress ? <p className="muted" data-testid="ci-progress">{urlProgress}…</p> : null}
          {result ? <ResultPanel result={result} onClose={onClose} /> : null}

          {!result && (
            <div className="row">
              <button type="submit" className="btn" data-testid="ci-import-url" disabled={!canImportUrl}>
                {busy ? 'Importing…' : 'Import Article'}
              </button>
              {error ? (
                <button
                  type="button"
                  className="btn secondary compact"
                  onClick={() => switchMode('paste')}
                  data-testid="ci-fallback-to-paste"
                >
                  Paste content instead
                </button>
              ) : null}
            </div>
          )}
        </form>
      )}

      {mode === 'paste' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (canPaste) void onPasteContent();
          }}
        >
          <p className="muted">Paste the text you want Spox to work from. Add the source URL if available.</p>
          <div className="field">
            <label htmlFor="ci-paste-url">Source URL (optional)</label>
            <input
              id="ci-paste-url"
              type="url"
              placeholder="https://example.com/article"
              value={pasteSourceUrl}
              onChange={(e) => setPasteSourceUrl(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ci-paste-content">Content</label>
            <textarea
              id="ci-paste-content"
              rows={8}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setResult(null);
                setError(null);
              }}
              required
            />
          </div>

          {error ? <p className="error">{error}</p> : null}
          {pasteProgress ? <p className="muted" data-testid="ci-progress">{pasteProgress}…</p> : null}
          {result ? <ResultPanel result={result} onClose={onClose} /> : null}

          {!result && (
            <div className="row">
              <button type="submit" className="btn" data-testid="ci-add-paste" disabled={!canPaste}>
                {busy ? 'Saving…' : 'Add Content'}
              </button>
            </div>
          )}
        </form>
      )}

      {mode === 'seo_package' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (canAddPackage) void onAddSeoPackage();
          }}
        >
          <p className="muted">
            Paste an SEO/social package generated before or after publication. Spox will preserve it and
            link it to the article when available.
          </p>
          <div className="field">
            <label htmlFor="ci-seo-url">Article URL (optional)</label>
            <input
              id="ci-seo-url"
              type="url"
              placeholder="https://example.com/article"
              value={articleUrl}
              onChange={(e) => setArticleUrl(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ci-seo-status">Publication status</label>
            <select
              id="ci-seo-status"
              value={publicationStatus}
              onChange={(e) => setPublicationStatus(e.target.value as PendingPackagePublicationStatus)}
            >
              <option value="not_published">Not yet published</option>
              <option value="published">Already published</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="ci-seo-package">SEO / Social Package</label>
            <textarea
              id="ci-seo-package"
              rows={10}
              value={rawPackage}
              onChange={(e) => {
                setRawPackage(e.target.value);
                setPackageSaved(false);
                setError(null);
              }}
              required
            />
          </div>

          {error ? <p className="error">{error}</p> : null}
          {seoProgress ? <p className="muted" data-testid="ci-progress">{seoProgress}…</p> : null}
          {packageSaved ? (
            <p className="ig-test-success" data-testid="ci-seo-saved">
              ✓ Package saved — Waiting for publication.
            </p>
          ) : null}

          {!packageSaved && (
            <div className="row">
              <button type="submit" className="btn" data-testid="ci-add-package" disabled={!canAddPackage}>
                {busy ? 'Saving…' : 'Add Package'}
              </button>
            </div>
          )}
          {packageSaved && (
            <div className="row">
              <Link className="btn secondary" to="/content-intake/pending" onClick={onClose}>
                View pending packages
              </Link>
              <button type="button" className="btn ghost compact" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}

function ResultPanel({ result, onClose }: { result: ContentIntakeSourceResult; onClose: () => void }) {
  return (
    <div className="ci-result" data-testid="ci-result">
      {result.duplicate ? (
        <p className="muted">This article is already in Spox.</p>
      ) : (
        <p className="ig-test-success">✓ Ready — {result.title || 'Untitled'}</p>
      )}
      <div className="row">
        <Link className="btn secondary" to={`/sources/${result.sourceId}`} onClick={onClose}>
          {result.duplicate ? 'Open Source' : 'View Source'}
        </Link>
        <Link className="btn ghost compact" to={`/sources/${result.sourceId}/brief`} onClick={onClose}>
          {result.duplicate ? 'Create/Open Campaign' : 'Create Campaign'}
        </Link>
      </div>
    </div>
  );
}
